import { useCallback, useEffect, useState } from "react"
import { Guid } from "@/types"
import AssetInspectionPair from "models/AssetInspectionPair"
import AssetService from "services/AssetService"

interface UseAssetInspectionPairsProps {
    assetId?: Guid
    reservationId?: Guid
}

interface UseAssetInspectionPairsResult {
    inspectionPairs: AssetInspectionPair[] | null
    isLoading: boolean
    error: Error | null
    refetch: () => Promise<void>
}

export function useAssetInspectionPairs({ assetId, reservationId }: UseAssetInspectionPairsProps): UseAssetInspectionPairsResult {
    const [inspectionPairs, setInspectionPairs] = useState<AssetInspectionPair[] | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<Error | null>(null)

    const fetchInspectionPairs = useCallback(async () => {
        if (!assetId) {
            setInspectionPairs(null)
            return
        }

        setIsLoading(true)
        setError(null)

        try {
            const pairs = await AssetService.getAssetInspectionPairs(assetId, reservationId)
            setInspectionPairs(pairs)
        } catch (err) {
            console.error('useAssetInspectionPairs: error', err)
            setError(err as Error)
            setInspectionPairs(null)
        } finally {
            setIsLoading(false)
        }
    }, [assetId, reservationId])

    const refetch = useCallback(async () => {
        await fetchInspectionPairs()
    }, [fetchInspectionPairs])

    useEffect(() => {
        fetchInspectionPairs()
    }, [fetchInspectionPairs])

    return {
        inspectionPairs,
        isLoading,
        error,
        refetch,
    }
}

export default useAssetInspectionPairs
